import { useState, useEffect } from "react";
import { Row, Col } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { LogIn, Mail, Phone } from "lucide-react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import bluelogo from "../assets/images/nttdata-logo.png";
import api from "../api";
import { useAuth } from "../auth/AuthContext";
import nttlogo from "../assets/images/adaptis-light.png";
import adaptis from "../assets/images/adaptis-dark.webp";

/* VALIDATION SCHEMA */
const LoginSchema = Yup.object({
  username: Yup.string().required("Username is required"),
  password: Yup.string().required("Password is required"),
});

export default function Login() {
  const navigate = useNavigate();

  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const { setToken, setUser, setRole } = useAuth();

  /* ✅ REDIRECT IF TOKEN EXISTS */
  useEffect(() => {
    const token = localStorage.getItem("jwt");
    const role = localStorage.getItem("role");

    if (token) {
      const restoreSession = async()=>{
      // restore session
      setToken(token);
      setRole(role);
      api.setToken(token);

      await api.get("/csrf");

      navigate("/dashboard");
    }
    restoreSession();
  }

  }, [navigate, setToken, setRole]);

  const handleSubmit = async (values) => {
    setErr("");
    setLoading(true);

   
    try {
       await api.get("/csrf");
      const { data } = await api.post("/auth/login", {
        username: values.username,
        password: values.password,
      });

      if (data.message === "Login SuccessFul") {
        // Save token + user in context
        setToken(data.token);
        setUser({ username: values.username });
        setRole(data.role);
        api.setToken(data.token);

        // Save in localStorage
        localStorage.setItem("jwt", data.token);
        localStorage.setItem("role", data.role);

        
        navigate("/dashboard");
      } else {
        setErr(data.message || "error");
      }
    } catch (e) {
      setErr(
        e.response?.data?.error ||
        e.response?.data?.message ||
        e.message
      );
    } finally {
      setLoading(false);
    }
  };

  /* ✅ OPTIONAL: prevent flashing UI */
  const existingToken = localStorage.getItem("jwt");
  if (existingToken) return null;

  return (
    <div className="eb-login">
      {/* LEFT */}
      <div className="eb-left d-flex flex-column text-center position-relative">
        <div className="eb-logo-cente mt-2">
          <img
            src={nttlogo}
            alt="NTT logo"
            className="img-fluid"
            style={{ width: "60%" }}
          />
        </div>

        <div className="eb-brand-info h-100 d-flex flex-column justify-content-center align-items-center">
          <h1 className="eb-brand-name">Generic Form Builder</h1>
          <p className="eb-brand-tagline">
            Build smarter forms in minutes — <br />
            for modern digital platforms
          </p>
          <span className="eb-brand-version">Version 2.1.2</span>
        </div>

        <div className="eb-help eb-help-bottom text-center">
          <div className="eb-footer-title">
            <span className="line" />
            <span className="text">Need help? Contact us</span>
            <span className="line" />
          </div>

          <Row>
            <Col sm="5">
              <span>
                <Phone size={18} /> 1860 266 4959
              </span>
            </Col>
            <Col sm="7">
              <span>
                <Mail size={18} /> NDPS.helpdesk@nttdata.com
              </span>
            </Col>
          </Row>
        </div>
      </div>

      {/* RIGHT */}
      <div className="eb-right h-100">
        <img
          src={adaptis}
          alt="NTT logo"
          className="img-fluid mb-5 d-md-none d-lg-none"
          style={{ width: "50%" }}
        />

        <div className="eb-card p-5 d-flex flex-column justify-content-center">
          <h1>Login</h1>
          <p>Use your credentials to login</p>

          <Formik
            initialValues={{ username: "", password: "" }}
            validationSchema={LoginSchema}
            onSubmit={handleSubmit}
          >
            {() => (
              <Form>
                {/* USERNAME */}
                <label htmlFor="username">Username *</label>
                <Field
                  id="username"
                  name="username"
                  type="text"
                  className="eb-input"
                />
                <ErrorMessage
                  name="username"
                  component="div"
                  className="eb-error"
                />

                {/* PASSWORD */}
                <label htmlFor="password">Password *</label>
                <div className="eb-pass">
                  <Field
                    id="password"
                    name="password"
                    type={show ? "text" : "password"}
                    className="eb-input"
                  />
                  <button type="button" onClick={() => setShow(!show)}>
                    {show ? "hide" : "show"}
                  </button>
                </div>

                <ErrorMessage
                  name="password"
                  component="div"
                  className="eb-error"
                />

                {/* ERROR */}
                {err && (
                  <div className="alert alert-danger py-2 px-3 mt-3 small rounded">
                    {err}
                  </div>
                )}

                {/* BUTTON */}
                <button
                  type="submit"
                  className={`eb-login-btn ${loading ? "loading" : ""}`}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="eb-btn-spinner" />
                  ) : (
                    <>
                      <LogIn size={16} className="eb-btn-icon" />
                      <span> Login</span>
                    </>
                  )}
                </button>
              </Form>
            )}
          </Formik>
        </div>

        <div className="eb-footer text-center">
          <span>
            © {new Date().getFullYear()} NTT DATA Payment Services India
          </span>
        </div>
      </div>
    </div>
  );
}
